import React, { useMemo, useState, useCallback, useRef } from 'react';
import { DataTable } from '../components/organisms';
import { useAdminUsers, useAdminUserActions } from '../hooks/useAdminUsers';
import type { Column, TableAction } from '../types/table';
import type { AdminUser, AdminUserDetail, UserTier, UserUpdateRequest } from '../types/api/admin-user';
import { formatDateForDisplay, formatTimeForDisplay, USER_TIERS, USER_GENDERS } from '../types/api/admin-user';
import { UserDetailModal } from '../components/organisms/UserDetailModal';
import { UserEditModal } from '../components/organisms/UserEditModal';
import { UserSoftDeleteModal } from '../components/organisms/UserSoftDeleteModal';
import { SubscriptionAssignmentModal } from '../components/organisms/SubscriptionAssignmentModal';
import { useSubscriptionActions } from '../hooks/useSubscriptions';
import type { SubscriptionCreateRequest } from '../types/api/subscription';
import toast from 'react-hot-toast';

/**
 * UserManagementPage Component
 * 
 * Admin page for managing users with comprehensive features:
 * - Server-side pagination
 * - Search functionality
 * - User details display
 * - Tier and status badges
 * - User actions (view, edit, suspend, delete)
 * - Export functionality
 * - Responsive design
 * 
 * Uses the DataTable component with custom hooks for data management
 */

export const UserManagementPage: React.FC = () => {
  const { fetchAdminUsers } = useAdminUsers();
  const { 
    getUserDetail, 
    updateUser, 
    softDeleteUser, 
    restoreUser, 
    suspendUser, 
    exportUsers 
  } = useAdminUserActions();
  
  // Subscription hooks
  const { createSubscription } = useSubscriptionActions();
  
  const refreshKeyRef = useRef(0);
  
  // Modal states
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  
  // Selected user states
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState<AdminUserDetail | null>(null);
  console.log("selectedUserDetail", selectedUserDetail);
  
  // Subscription plans state
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  
  // Loading state for detail fetch
  const [, setIsLoadingDetail] = useState(false);
  
  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const columns = useMemo<Column<AdminUser>[]>(
    () => [
      {
        id: 'name',
        header: 'User',
        accessor: 'name',
        cell: (row) => (
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#EAB308] to-[#CA8A04] flex items-center justify-center text-white font-semibold flex-shrink-0">
              {(row.name ?? '').charAt(0).toUpperCase() || '?'}
            </div>
            {/* User Info */}
            <div>
              <p className="text-sm font-semibold text-gray-900">{row.name}</p>
              <p className="text-xs text-gray-500">{row.email}</p>
            </div>
          </div>
        ),
        minWidth: '250px',
      },
      {
        id: 'phone',
        header: 'Phone',
        accessor: 'phone',
        cell: (row) => (
          <span className="text-sm text-gray-900">{row.phone || '-'}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'gender',
        header: 'Gender',
        accessor: 'gender',
        cell: (row) => {
          const genderColors: Record<string, string> = {
            MALE: 'bg-blue-100 text-blue-800',
            FEMALE: 'bg-pink-100 text-pink-800',
            OTHER: 'bg-purple-100 text-purple-800',
            PREFER_NOT_TO_SAY: 'bg-gray-100 text-gray-800',
          };
          const gender = row.gender ?? '';
          
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                genderColors[row.gender ?? ''] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {gender ? gender.charAt(0) + gender.slice(1).toLowerCase().replace('_', ' ') : '-'}
            </span>
          );
        },
        hideOnMobile: true,
      },
      {
        id: 'tier',
        header: 'Tier',
        accessor: 'tier',
        cell: (row) => {
          const tierConfig: Record<UserTier, { color: string; icon: string }> = {
            FREE: { color: 'bg-gray-100 text-gray-800', icon: '🆓' },
            PREMIUM: { color: 'bg-yellow-100 text-yellow-800', icon: '⭐' },
            VIP: { color: 'bg-purple-100 text-purple-800', icon: '👑' },
          };
          
          const config = tierConfig[row.tier] || tierConfig.FREE;
          
          return (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
            >
              <span>{config.icon}</span>
              <span>{row.tier}</span>
            </span>
          );
        },
      },
      {
        id: 'registrationDate',
        header: 'Registered',
        accessor: 'registrationDate',
        cell: (row) => (
          <div>
            <p className="text-sm text-gray-900">{formatDateForDisplay(row.registrationDate)}</p>
            <p className="text-xs text-gray-500">{formatTimeForDisplay(row.registrationDate)}</p>
          </div>
        ),
        hideOnMobile: true,
      },
      {
        id: 'lastLogin',
        header: 'Last Login',
        accessor: 'lastLogin',
        cell: (row) => {
          if (!row.lastLogin) {
            return <span className="text-sm text-gray-400 italic">Never</span>;
          }
          
          // Normalize date string for proper parsing (treat timezone-less as UTC)
          const normalizedString = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(row.lastLogin)
            ? row.lastLogin + 'Z'
            : row.lastLogin;
          const loginDate = new Date(normalizedString);
          
          const daysSinceLogin = Math.floor(
            (Date.now() - loginDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          
          const formattedDate = formatDateForDisplay(row.lastLogin);
          
          return (
            <div>
              <p className="text-sm text-gray-900">{formattedDate}</p>
              <p className="text-xs text-gray-500">
                {daysSinceLogin === 0
                  ? 'Today'
                  : daysSinceLogin === 1
                  ? 'Yesterday'
                  : `${daysSinceLogin} days ago`}
              </p>
            </div>
          );
        },
        hideOnMobile: true,
      },
    ],
    []
  );


  /**
   * Handle view user - fetch detail and open modal
   */
  const handleViewUser = useCallback(async (user: AdminUser) => {
    setSelectedUser(user);
    setIsLoadingDetail(true);
    
    // Show loading toast for immediate feedback
    const loadingToast = toast.loading('Loading user details...');
    
    try {
      const detail = await getUserDetail(user.userId);
      console.log("detail from View", detail);
      setSelectedUserDetail(detail);
      // Only open modal AFTER data is loaded
      setIsDetailModalOpen(true);
      toast.dismiss(loadingToast);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to load user details');
    } finally {
      setIsLoadingDetail(false);
    }
  }, [getUserDetail]);

  /**
   * Handle edit user - fetch detail and open edit modal
   */
  const handleEditUser = useCallback(async (user: AdminUser) => {
    setSelectedUser(user);
    setIsLoadingDetail(true);
    
    // Show loading toast for immediate feedback
    const loadingToast = toast.loading('Loading user details...');
    
    try {
      const detail = await getUserDetail(user.userId);
      console.log("detail from Edit", detail);
      setSelectedUserDetail(detail);
      // Only open modal AFTER data is loaded and state is set
      setIsEditModalOpen(true);
      toast.dismiss(loadingToast);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to load user details');
    } finally {
      setIsLoadingDetail(false);
    }
  }, [getUserDetail]);

  /**
   * Handle delete user - open delete modal
   */
  const handleDeleteUser = useCallback((user: AdminUser) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Handle edit from detail modal
   */
  const handleEditFromDetail = useCallback((user: AdminUserDetail) => {
    setSelectedUserDetail(user);
    setIsDetailModalOpen(false);
    setIsEditModalOpen(true);
  }, []);

  /**
   * Handle delete from detail modal
   */
  const handleDeleteFromDetail = useCallback((user: AdminUserDetail) => {
    setSelectedUser(user as AdminUser);
    setIsDetailModalOpen(false);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Handle restore from detail modal
   */
  const handleRestoreFromDetail = useCallback(async (user: AdminUserDetail) => {
    try {
      await restoreUser(user.userId);
      toast.success('User restored successfully');
      setIsDetailModalOpen(false);
      handleRefresh();
    } catch (error) {
      toast.error('Failed to restore user');
    }
  }, [restoreUser, handleRefresh]);

  /**
   * Handle edit submit
   */
  const handleEditSubmit = useCallback(async (data: { payload: UserUpdateRequest; profileImage: File | null }) => {
    if (!selectedUserDetail) return;
    
    try {
      await updateUser(selectedUserDetail.userId, data.payload, data.profileImage);
      toast.success('User updated successfully');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      setSelectedUserDetail(null);
      handleRefresh();
    } catch (error) {
      toast.error('Failed to update user');
      throw error;
    }
  }, [selectedUserDetail, updateUser, handleRefresh]);

  /**
   * Handle delete confirm
   */
  const handleDeleteConfirm = useCallback(async (reason: string) => {
    if (!selectedUser) return;
    
    try {
      await softDeleteUser(selectedUser.userId, reason);
      toast.success('User deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      handleRefresh();
    } catch (error) {
      toast.error('Failed to delete user');
      throw error;
    }
  }, [selectedUser, softDeleteUser, handleRefresh]);

  /**
   * Handle suspend user
   */
  const handleSuspendUser = useCallback(async (user: AdminUser) => {
    if (window.confirm(`Are you sure you want to suspend user "${user.name}"?`)) {
      try {
        await suspendUser(user);
        toast.success('User suspended successfully');
        handleRefresh();
      } catch (error) {
        toast.error('Failed to suspend user');
      }
    }
  }, [suspendUser, handleRefresh]);

  /**
   * Handle upgrade user - open subscription assignment modal
   */
  const handleUpgradeUser = useCallback(async (user: AdminUser) => {
    setSelectedUser(user);
    
    const loadingToast = toast.loading('Loading subscription plans...');
    
    try {
      // Fetch active subscription plans
      const { getSubscriptionService } = await import('../service/ServiceRegistry');
      const subscriptionService = getSubscriptionService();
      const response = await subscriptionService.getSubscriptionPlans({ isActive: true, size: 100 });
      
      setSubscriptionPlans(response.items);
      setIsUpgradeModalOpen(true);
      toast.dismiss(loadingToast);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to load subscription plans');
    }
  }, []);

  /**
   * Handle subscription assignment submit
   */
  const handleUpgradeSubmit = useCallback(async (data: SubscriptionCreateRequest) => {
    try {
      await createSubscription(data);
      setIsUpgradeModalOpen(false);
      setSelectedUser(null);
      handleRefresh();
    } catch (error) {
      // Error already handled in hook with toast
      throw error;
    }
  }, [createSubscription, handleRefresh]);

  const actions = useMemo<TableAction<AdminUser>[]>(
    () => [
      {
        id: 'view',
        label: 'View',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleViewUser,
      },
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEditUser,
      },
      {
        id: 'upgrade',
        label: 'Upgrade',
        variant: 'secondary',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
            <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
          </svg>
        ),
        onClick: handleUpgradeUser,
      },
      // {
      //   id: 'suspend',
      //   label: 'Suspend',
      //   variant: 'secondary',
      //   icon: (
      //     <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      //       <path
      //         fillRule="evenodd"
      //         d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z"
      //         clipRule="evenodd"
      //       />
      //     </svg>
      //   ),
      //   onClick: handleSuspendUser,
      // },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDeleteUser,
      },
    ],
    [handleViewUser, handleEditUser, handleUpgradeUser, handleSuspendUser, handleDeleteUser]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1 text-left pl-3">Manage and monitor all platform users</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={exportUsers}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Export
          </button>
          
          {/* <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#EAB308] rounded-lg hover:bg-[#CA8A04] focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 shadow-md hover:shadow-lg transition-all">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add User
          </button> */}
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-600">Premium Users</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-600">New This Month</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* DataTable */}
      <DataTable<AdminUser>
        columns={columns}
        fetchData={fetchAdminUsers}
        searchable
        searchPlaceholder="Search by name..."
        initialPageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        key={refreshKeyRef.current}
        caption="User management table"
        renderHeaderActions={({ setFilter, removeFilter, clearFilters, filters }) => {
          const tierValue = filters.find(f => f.columnId === 'tier')?.value || '';
          const genderValue = filters.find(f => f.columnId === 'gender')?.value || '';
          const dateFromValue = filters.find(f => f.columnId === 'registration_date_from')?.value || '';
          const dateToValue = filters.find(f => f.columnId === 'registration_date_to')?.value || '';
          const hasActiveFilters = filters.length > 0;

          return (
            <div className="flex items-center gap-3 flex-wrap">
              {/* Tier Filter */}
              <div className="flex items-center gap-2">
                <label htmlFor="tier-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Tier:
                </label>
                <select
                  id="tier-filter"
                  value={tierValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      removeFilter('tier');
                    } else {
                      setFilter({ columnId: 'tier', operator: 'equals', value });
                    }
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
                >
                  {USER_TIERS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="h-6 w-px bg-gray-200" />

              {/* Gender Filter */}
              <div className="flex items-center gap-2">
                <label htmlFor="gender-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Gender:
                </label>
                <select
                  id="gender-filter"
                  value={genderValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      removeFilter('gender');
                    } else {
                      setFilter({ columnId: 'gender', operator: 'equals', value });
                    }
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
                >
                  {USER_GENDERS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>

              <div className="h-6 w-px bg-gray-200" />

              {/* Registration Date Range */}
              <div className="flex items-center gap-2">
                <label htmlFor="date-from-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Registered:
                </label>
                <input
                  id="date-from-filter"
                  type="date"
                  value={dateFromValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      removeFilter('registration_date_from');
                    } else {
                      setFilter({ columnId: 'registration_date_from', operator: 'greaterThanOrEqual', value });
                    }
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
                />
                <span className="text-sm text-gray-400">to</span>
                <input
                  id="date-to-filter"
                  type="date"
                  value={dateToValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      removeFilter('registration_date_to');
                    } else {
                      setFilter({ columnId: 'registration_date_to', operator: 'lessThanOrEqual', value });
                    }
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
                />
              </div>

              {hasActiveFilters && (
                <>
                  <div className="h-6 w-px bg-gray-200" />
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300/30 transition-colors"
                  >
                    Clear Filters
                  </button>
                </>
              )}
            </div>
          );
        }}
      />

      {/* User Detail Modal */}
      <UserDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedUser(null);
          setSelectedUserDetail(null);
        }}
        user={selectedUserDetail}
        onEdit={handleEditFromDetail}
        onDelete={handleDeleteFromDetail}
        onRestore={handleRestoreFromDetail}
      />

      {/* User Edit Modal */}
      <UserEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
          setSelectedUserDetail(null);
        }}
        onSubmit={handleEditSubmit}
        user={selectedUserDetail || undefined}
      />

      {/* User Soft Delete Modal */}
      <UserSoftDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteConfirm}
        user={selectedUser}
      />

      {/* Subscription Assignment Modal (Upgrade) */}
      <SubscriptionAssignmentModal
        isOpen={isUpgradeModalOpen}
        onClose={() => {
          setIsUpgradeModalOpen(false);
          setSelectedUser(null);
          setSubscriptionPlans([]);
        }}
        onSubmit={handleUpgradeSubmit}
        userId={selectedUser?.userId}
        userName={selectedUser?.name}
        subscriptionPlans={subscriptionPlans}
      />
    </div>
  );
};

export default UserManagementPage;

